import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Info, RefreshCw, X } from 'lucide-react';
import {
  GameWalletTransactionDirection,
  GameWalletTransactionStatus,
  ProviderTransactionFilters,
} from '../../../../types';
import { useProviderGameWalletTransactions } from '../../../../hooks/useApi';
import { useFormatDate } from '../../../../hooks/useFormatDate';
import { cn, formatCurrency } from '../../../../utils';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../ui/Table';
import Button from '../../../ui/Button';
import { LoadingSpinner } from '../../../ui/Loading';
import { NumberedPagination, SortableTableHead } from '@kioskgaming/ui';
import useTableSort from '../../../../hooks/useTableSort';
import { getTableSortColumns } from '../../../../config/tableSortConfig';
import type { SortDirection } from '@kioskgaming/ui/dist/data-table/sortState';

interface TransactionsTabProps {
  providerId: string;
  providerName?: string;
}

const directionLabel: Record<GameWalletTransactionDirection, string> = {
  master_to_game: 'Game Wallet Deposit',
  game_to_master: 'Game Wallet Recall',
  adjustment: 'Adjustment',
};

const statusColors: Record<GameWalletTransactionStatus, string> = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  processing: 'bg-blue-50 text-blue-700 border-blue-200',
  completed: 'bg-green-50 text-green-700 border-green-200',
  failed: 'bg-red-50 text-red-800 border-red-200',
};

const overallStatusColors: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  processing: 'bg-blue-50 text-blue-700 border-blue-200',
  completed: 'bg-green-50 text-green-700 border-green-200',
  failed: 'bg-red-50 text-red-800 border-red-200',
  cancelled: 'bg-gray-100 text-gray-600 border-gray-200',
  approved: 'bg-green-50 text-green-700 border-green-200',
  rejected: 'bg-red-50 text-red-800 border-red-200',
  in_review_with_provider: 'bg-violet-50 text-violet-800 border-violet-200',
  awaiting_admin: 'bg-orange-50 text-orange-800 border-orange-200',
};

const formatOverallStatus = (status: string): string => {
  if (status === 'in_review_with_provider') {
    return 'Legacy provider review';
  }
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const TransactionsTab: React.FC<TransactionsTabProps> = ({ providerId, providerName }) => {
  const fmt = useFormatDate();
  const [page, setPage] = useState(1);
  const [direction, setDirection] = useState<GameWalletTransactionDirection | ''>('');
  const [status, setStatus] = useState<GameWalletTransactionStatus | ''>('');

  const tableSort = useTableSort({
    columns: getTableSortColumns('providerWalletTransactions'),
    paramPrefix: 'providerTx',
    onChange: () => setPage(1),
  });

  const queryFilters = useMemo((): ProviderTransactionFilters => {
    const filters: ProviderTransactionFilters = {
      gameProviderId: providerId,
      page,
      limit: 50,
      direction: direction || undefined,
      status: status || undefined,
    };
    const { sortBy, sortOrder } = tableSort.sortParams;
    if (sortBy === 'amount' || sortBy === 'createdAt') {
      filters.sortBy = sortBy;
      filters.sortOrder = sortOrder;
    }
    return filters;
  }, [providerId, page, direction, status, tableSort.sortParams]);

  const { data, isLoading, isFetching, error, refetch } = useProviderGameWalletTransactions(queryFilters);

  const items = data?.data?.items ?? [];
  const pagination = data?.data ?? { total: 0, page: 1, limit: 50 };
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  const appliedSort = useMemo(
    () => ({
      sortBy: (data?.data?.sortBy ?? null) as string | null,
      sortOrder: (data?.data?.sortOrder ?? null) as SortDirection | null,
    }),
    [data?.data?.sortBy, data?.data?.sortOrder],
  );

  const headerSort = tableSort.sortBy
    ? {
        activeKey: appliedSort.sortBy ?? tableSort.sortBy,
        direction: appliedSort.sortOrder ?? tableSort.sortOrder,
      }
    : { activeKey: null, direction: null };

  const isTableBusy = isFetching && items.length > 0;

  useEffect(() => {
    setPage(1);
  }, [providerId]);

  const handleClear = () => {
    setDirection('');
    setStatus('');
    setPage(1);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight">Transactions</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Game wallet movements only. These should not be counted as external player deposits or withdrawals.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
          <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
        </Button>
      </div>

      <div className="flex items-start gap-3 rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
        <Info className="h-5 w-5 mt-0.5 shrink-0 text-blue-500" />
        <p>
          <strong>Statistics rule:</strong> Game Wallet Deposit and Game Wallet Recall are{' '}
          <strong>internal wallet movements</strong>. Do not count them in Total External Player
          Deposits or Total External Player Withdrawals.
        </p>
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <div className="min-w-[180px]">
          <label className="block text-xs font-medium text-muted-foreground mb-1">Type</label>
          <select
            value={direction}
            onChange={(e) => { setDirection(e.target.value as GameWalletTransactionDirection | ''); setPage(1); }}
            className="w-full h-10 px-3 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">All types</option>
            <option value="master_to_game">Game Wallet Deposit</option>
            <option value="game_to_master">Game Wallet Recall</option>
            <option value="adjustment">Adjustment</option>
          </select>
        </div>

        <div className="min-w-[150px]">
          <label className="block text-xs font-medium text-muted-foreground mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value as GameWalletTransactionStatus | ''); setPage(1); }}
            className="w-full h-10 px-3 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">All status</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
        </div>

        {(direction || status) && (
          <Button variant="outline" size="sm" onClick={handleClear}>
            <X className="h-4 w-4 mr-1" />
            Clear
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center h-48 gap-2">
          <p className="text-destructive text-sm">Failed to load transactions</p>
          <button type="button" onClick={() => refetch()} className="text-xs text-primary hover:underline">Try again</button>
        </div>
      ) : (
        <>
          <div
            className={cn(
              'rounded-md border overflow-x-auto transition-opacity',
              isTableBusy && 'opacity-60',
            )}
            aria-busy={isTableBusy || undefined}
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Transaction ID</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Player</TableHead>
                  <SortableTableHead
                    sortable
                    label="Amount"
                    sortKey="amount"
                    activeKey={headerSort.activeKey}
                    direction={headerSort.direction}
                    onSort={tableSort.toggleSort}
                    align="right"
                    magnitude
                  />
                  <TableHead>Overall Status</TableHead>
                  <TableHead>Game Sync Status</TableHead>
                  <SortableTableHead
                    sortable
                    label="Created"
                    sortKey="createdAt"
                    activeKey={headerSort.activeKey}
                    direction={headerSort.direction}
                    onSort={tableSort.toggleSort}
                  />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-12">
                      No transactions found{providerName ? ` for ${providerName}` : ''}
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((tx) => {
                    const overallStatus = tx.paymentTransaction?.status;
                    return (
                      <TableRow key={tx.id}>
                        <TableCell>
                          <span className="font-mono text-xs text-muted-foreground">
                            {tx.id.slice(0, 8)}…
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm font-medium">
                            {directionLabel[tx.direction] ?? tx.direction}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Link
                            to={`/users/${encodeURIComponent(tx.userId)}`}
                            className="block max-w-[14rem] truncate text-sm font-medium text-primary hover:underline"
                          >
                            {(tx as { user?: { email?: string; phone?: string } }).user?.email
                              || (tx as { user?: { email?: string; phone?: string } }).user?.phone
                              || tx.userId.slice(0, 8)}
                          </Link>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatCurrency(parseFloat(String(tx.amount)))}
                        </TableCell>
                        <TableCell>
                          {overallStatus ? (
                            <span className={cn(
                              'px-2 py-0.5 rounded-full text-xs font-semibold border',
                              overallStatusColors[overallStatus] ?? 'bg-gray-100 text-gray-700 border-gray-200',
                            )}>
                              {formatOverallStatus(overallStatus)}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className={cn(
                            'px-2 py-0.5 rounded-full text-xs font-semibold border',
                            statusColors[tx.status] ?? 'bg-gray-100 text-gray-700',
                          )}>
                            {tx.status.charAt(0).toUpperCase() + tx.status.slice(1)}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {tx.created_at ? fmt(tx.created_at) : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-between items-center text-sm text-muted-foreground">
              <span>
                Showing {items.length} of {pagination.total} transactions
              </span>
              <NumberedPagination
                currentPage={pagination.page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
          {totalPages <= 1 && items.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {pagination.total} transaction{pagination.total !== 1 ? 's' : ''} total
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default TransactionsTab;
